import { Map } from 'immutable'
import { AsyncState, loadedAsyncState, loadingAsyncState, mk_pair, Pair } from 'widgets-for-react'
import { ImageSize, Url } from '../../cms/blocks/types.gen'
import { PairAsyncImage } from '../../cms/state/types.gen'
import { isCustomBlock } from '../../public_site/blocks/custom_blocks/types.mod'
import { ExtraImages } from '../../public_site/utils/extra_images.mod'
import { CustomBlock } from '../types_and_parser/custom_blocks.gen'
import { Block } from '../types_and_parser/standard_blocks.gen'
import { WordPress } from '../wordpress_api'

export function loadMedia(image: number): AsyncState<Map<ImageSize<ExtraImages>, Url>> {
  return loadingAsyncState<Map<ImageSize<ExtraImages>, Url>>(_ => {
    return WordPress.media<WordPress.MediaResponse>()
      .findById(image)
      .then(response => {
        const sizes = response.data.media_details.sizes

        // Set default WP sizes.
        let value = Map<ImageSize<ExtraImages>, Url>().set('full', sizes.full.source_url)
        if (sizes.thumbnail) {
          value = value.set('thumbnail', sizes.thumbnail.source_url)
        }
        if (sizes.medium) {
          value = value.set('medium', sizes.medium.source_url)
        }
        if (sizes.medium_large) {
          value = value.set('medium_large', sizes.medium_large.source_url)
        }
        if (sizes.large) {
          value = value.set('large', sizes.large.source_url)
        }

        let customSizes: Array<ImageSize<ExtraImages>> = [
          '580w',
          '210w',
          'super_grid',
          'tab',
          'media_block_large',
          'media_block_small',
          'featured_content',
          '650x430',
          '1300x860',
          '1625x1075'
        ]

        customSizes.forEach(size => {
          if (sizes[size]) {
            value = value.set(size, sizes[size].source_url)
          }
        })

        return { status: 200, value, kind: 'result' as 'result' }
      })
      .catch(error => {
        console.error(error)
        return { status: 400, kind: 'failed' as 'failed' }
      })
  })
}

export function loadedMedia(sizes: { [P in ImageSize<ExtraImages>]: string }): AsyncState<Map<ImageSize<ExtraImages>, Url>> {
  let map = Map<ImageSize<ExtraImages>, Url>()
  Object.entries(sizes).forEach(([key, value]) => {
    map = value != undefined ? map.set(<any>key, value) : map
  })
  return loadedAsyncState<Map<ImageSize<ExtraImages>, Url>>(map)
}

export const WordPressBlockImagesLoader = (
  block: Block | CustomBlock,
  get_images_from_custom_block?: (b: CustomBlock) => Array<PairAsyncImage<ExtraImages>>
): Array<Pair<number, AsyncState<Map<ImageSize<ExtraImages>, Url>>>> => {
  if (block.kind === 'featured_content_block' && block.image) {
    return [mk_pair(block.image, loadMedia(block.image))]
  }

  if (block.kind === 'link_grid_block') {
    return block.items
      .filter(x => x.image)
      .map(b => mk_pair(b.image, loadMedia(b.image) as AsyncState<Map<ImageSize<ExtraImages>, Url>>))
  }

  if (block.kind === 'super_grid') {
    return block.items
      .filter(x => x.image)
      .map(b => mk_pair(b.image, loadMedia(b.image) as AsyncState<Map<ImageSize<ExtraImages>, Url>>))
  }

  if (block.kind === 'tabs') {
    return block.items
      .filter(x => x.image)
      .map(b => mk_pair(b.image, loadMedia(b.image) as AsyncState<Map<ImageSize<ExtraImages>, Url>>))
  }

  if (block.kind === 'showcase') {
    return block.items
      .filter(x => x.image)
      .map(b => mk_pair(b.image, loadMedia(b.image) as AsyncState<Map<ImageSize<ExtraImages>, Url>>))
  }

  if (block.kind === 'contactperson' && block.image) {
    return [mk_pair(block.image, loadMedia(block.image))]
  }

  if (block.kind === 'media_block' && block.image) {
    return [mk_pair(block.image, loadMedia(block.image))]
  }

  if (block.kind === 'highlight_article') {
    let images : Array<Pair<number, AsyncState<Map<ImageSize<ExtraImages>, Url>>>> = []
    if (block.article_left.acf && block.article_left.acf.image) {
      images = [ ...images, mk_pair(block.article_left.acf.image.id, loadedMedia(block.article_left.acf.image.sizes)) ]
    }
    if (block.articles_right) {
      block.articles_right.forEach(x => {
        if (x.acf && x.acf.image) {
          images = [ ...images, mk_pair(x.acf.image.id, loadedMedia(x.acf.image.sizes)) ]
        }
      })
    }

    return images
  }

    


  if (get_images_from_custom_block && isCustomBlock(block)) {
    return get_images_from_custom_block(block)
  }

  return []
}
